"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { Settings } from "lucide-react";
import { cn } from "@/lib/utils";

export type SettingsSection =
  | "appearance"
  | "learning"
  | "notifications"
  | "pass"
  | "purchases"
  | "account";
const Context = createContext<{
  section: SettingsSection | null;
  openSettings: (section?: SettingsSection) => void;
  closeSettings: () => void;
  restoreFocus: () => void;
} | null>(null);
const SettingsDialog = dynamic(() => import("./SettingsDialog"), {
  ssr: false,
});

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [section, setSection] = useState<SettingsSection | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const openSettings = useCallback((next: SettingsSection = "appearance") => {
    if (
      document.activeElement instanceof HTMLElement &&
      !document.activeElement.closest("[data-settings-dialog]")
    ) {
      trigger.current = document.activeElement;
    }
    setSection(next);
  }, []);
  const closeSettings = useCallback(() => setSection(null), []);
  const restoreFocus = useCallback(() => {
    if (trigger.current?.isConnected && trigger.current !== document.body) {
      trigger.current.focus();
      return;
    }
    // A dropdown item or desktop rail may have unmounted while settings were open.
    const fallback = Array.from(
      document.querySelectorAll<HTMLElement>("[data-settings-trigger]"),
    ).find((element) => element.getClientRects().length > 0);
    fallback?.focus();
  }, []);
  const value = useMemo(
    () => ({ section, openSettings, closeSettings, restoreFocus }),
    [section, openSettings, closeSettings, restoreFocus],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSettings() {
  const value = useContext(Context);
  if (!value) throw new Error("SettingsProvider is required");
  return value;
}

export function SettingsHost() {
  const { section } = useSettings();
  return section ? <SettingsDialog /> : null;
}

export function SettingsButton({ showLabel = false }: { showLabel?: boolean }) {
  const { openSettings } = useSettings();
  return (
    <button
      type="button"
      onClick={() => openSettings()}
      title="설정"
      aria-label="설정 열기"
      data-settings-trigger
      className={cn(
        "flex h-[42px] shrink-0 items-center gap-3 rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        showLabel ? "w-full px-3 text-sm" : "w-[42px] justify-center",
      )}
    >
      <Settings className="h-[18px] w-[18px]" aria-hidden />
      {showLabel && <span>설정</span>}
    </button>
  );
}
